import React, { useMemo } from 'react';

/**
 * Seven-segment neon countdown — adapted from AI Canvas Neon Clock styling.
 * Displays game timer remaining time (MM:SS or H:MM:SS) in brand purple.
 * https://aicanvas.me/components/neon-clock
 */

const PURPLE = '#D702F6';
const PURPLE_OFF = 'rgba(215, 2, 246, 0.055)';
const RED = '#FF3B30';
const RED_OFF = 'rgba(255, 59, 48, 0.055)';
const GREEN = '#4CAF50';
const GREEN_OFF = 'rgba(76, 175, 80, 0.055)';
const BG = 'transparent';

const GLOW_PURPLE = 'drop-shadow(0 0 3px #D702F6) drop-shadow(0 0 8px rgba(215,2,246,0.6))';
const GLOW_RED = 'drop-shadow(0 0 3px #FF3B30) drop-shadow(0 0 8px rgba(255,59,48,0.55))';
const GLOW_GREEN = 'drop-shadow(0 0 3px #4CAF50) drop-shadow(0 0 8px rgba(76,175,80,0.55))';

const VW = 42;
const VH = 80;
const T = 6;
const BV = 3;
const GP = 2;

const hPts = (y) =>
  `${GP},${y + T / 2} ${GP + BV},${y} ${VW - GP - BV},${y} ${VW - GP},${y + T / 2} ${VW - GP - BV},${y + T} ${GP + BV},${y + T}`;

const vPts = (x, y1, y2) => {
  const cx = x + T / 2;
  return `${cx},${y1} ${x + T},${y1 + BV} ${x + T},${y2 - BV} ${cx},${y2} ${x},${y2 - BV} ${x},${y1 + BV}`;
};

const aY = GP;
const gY = VH / 2 - T / 2;
const dY = VH - GP - T;
const lX = GP;
const rX = VW - GP - T;
const aEnd = aY;
const gTop = gY - GP;
const gEnd = gY + T + GP;
const dTop = dY + T;

const SHAPES = [
  hPts(aY),
  vPts(rX, aEnd, gTop),
  vPts(rX, gEnd, dTop),
  hPts(dY),
  vPts(lX, gEnd, dTop),
  vPts(lX, aEnd, gTop),
  hPts(gY)
];

const SEG = {
  '0': [1, 1, 1, 1, 1, 1, 0],
  '1': [0, 1, 1, 0, 0, 0, 0],
  '2': [1, 1, 0, 1, 1, 0, 1],
  '3': [1, 1, 1, 1, 0, 0, 1],
  '4': [0, 1, 1, 0, 0, 1, 1],
  '5': [1, 0, 1, 1, 0, 1, 1],
  '6': [1, 0, 1, 1, 1, 1, 1],
  '7': [1, 1, 1, 0, 0, 0, 0],
  '8': [1, 1, 1, 1, 1, 1, 1],
  '9': [1, 1, 1, 1, 0, 1, 1],
  '-': [0, 0, 0, 0, 0, 0, 1]
};

function Digit({ char, size, onColor, offColor, glow }) {
  const segs = SEG[char] || SEG['-'];
  const height = Math.round(size * (VH / VW));
  return (
    <svg
      viewBox={`0 0 ${VW} ${VH}`}
      width={size}
      height={height}
      style={{ display: 'block', overflow: 'visible', flexShrink: 0 }}
      aria-hidden="true"
    >
      {SHAPES.map((pts, i) => (
        <polygon
          key={i}
          points={pts}
          fill={segs[i] ? onColor : offColor}
          style={segs[i] ? { filter: glow } : undefined}
        />
      ))}
    </svg>
  );
}

function ColonDots({ dim, size, onColor, offColor, glow }) {
  const w = Math.round(size * 0.44);
  const height = Math.round(size * (VH / VW));
  const fill = dim ? offColor : onColor;
  return (
    <svg
      viewBox={`0 0 ${VW * 0.44} ${VH}`}
      width={w}
      height={height}
      style={{ display: 'block', overflow: 'visible', flexShrink: 0 }}
      aria-hidden="true"
    >
      <circle
        cx={VW * 0.22}
        cy={VH * 0.3}
        r={VW * 0.125}
        fill={fill}
        style={!dim ? { filter: glow } : undefined}
      />
      <circle
        cx={VW * 0.22}
        cy={VH * 0.68}
        r={VW * 0.125}
        fill={fill}
        style={!dim ? { filter: glow } : undefined}
      />
    </svg>
  );
}

function pad2(n) {
  return String(Math.max(0, n)).padStart(2, '0');
}

/**
 * @param {object} props
 * @param {number|null|undefined} props.seconds - Remaining seconds (null shows 00:00 or placeholder)
 * @param {'sm'|'md'|'lg'} [props.size]
 * @param {boolean} [props.urgent] - Use red urgent palette
 * @param {'default'|'urgent'|'complete'} [props.tone]
 * @param {boolean} [props.showHours] - Force HH:MM:SS when true, or auto when >= 1 hour
 * @param {string} [props.className]
 * @param {object} [props.style]
 */
function NeonCountdown({
  seconds = 0,
  size = 'lg',
  urgent = false,
  tone,
  showHours,
  className = '',
  style = {}
}) {
  const total = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;

  const parts = useMemo(() => {
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const useHours = showHours === true || (showHours !== false && h > 0);
    if (useHours) {
      return {
        useHours: true,
        h: pad2(h),
        m: pad2(m),
        s: pad2(s)
      };
    }
    // Minutes may exceed 99 for long sessions
    const mins = Math.floor(total / 60);
    return {
      useHours: false,
      m: String(mins).padStart(2, '0'),
      s: pad2(s)
    };
  }, [total, showHours]);

  const digitSize = size === 'sm' ? 22 : size === 'md' ? 36 : 50;
  const gap = size === 'sm' ? 2 : 3;
  const resolvedTone = tone || (urgent ? 'urgent' : 'default');
  const onColor =
    resolvedTone === 'complete' ? GREEN : resolvedTone === 'urgent' ? RED : PURPLE;
  const offColor =
    resolvedTone === 'complete' ? GREEN_OFF : resolvedTone === 'urgent' ? RED_OFF : PURPLE_OFF;
  const glow =
    resolvedTone === 'complete' ? GLOW_GREEN : resolvedTone === 'urgent' ? GLOW_RED : GLOW_PURPLE;

  const renderDigits = (str) =>
    str.split('').map((ch, i) => (
      <Digit
        key={`${str}-${i}`}
        char={ch}
        size={digitSize}
        onColor={onColor}
        offColor={offColor}
        glow={glow}
      />
    ));

  return (
    <div
      className={className}
      role="timer"
      aria-live="polite"
      aria-label={`${parts.useHours ? `${parts.h} hours ` : ''}${parts.m} minutes ${parts.s} seconds remaining`}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        select: 'none',
        userSelect: 'none',
        background: BG,
        padding: size === 'sm' ? '0.15rem 0.35rem' : '0.35rem 0.5rem',
        ...style
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap,
          position: 'relative',
          zIndex: 1
        }}
      >
        {parts.useHours && (
          <>
            {renderDigits(parts.h)}
            <ColonDots
              dim={false}
              size={digitSize}
              onColor={onColor}
              offColor={offColor}
              glow={glow}
            />
          </>
        )}
        {renderDigits(parts.m)}
        <ColonDots
          dim={false}
          size={digitSize}
          onColor={onColor}
          offColor={offColor}
          glow={glow}
        />
        {renderDigits(parts.s)}
      </div>

      {/* LCD pixel-grid overlay */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          backgroundImage:
            'radial-gradient(circle, rgba(0,0,0,0.48) 1.3px, transparent 1.3px)',
          backgroundSize: '3.8px 3.8px',
          opacity: 0.55,
          borderRadius: 4,
          zIndex: 2
        }}
      />
    </div>
  );
}

export default NeonCountdown;
